import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {Box, VStack, HStack, Link, useMultiStyleConfig, StackProps, LinkProps, TextProps, Skeleton} from '@chakra-ui/react'
import {Link as RouteLink} from 'react-router-dom'
import {Tag} from '_uiComponents'
import {useAnalytics, useViewport} from '_customHooks'
import {useProductTileAnalyticsContext, useLoader} from '_context'
import {useProduct} from '@salesforce/commerce-sdk-react'
import useEinstein from '_customHooks/use-einstein'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {TILE_ASSET_SIZE, updateCloudinarySize} from '_utils'
import {getProductAnalyticsInfo, getProductImpressionAnalytics} from '_analytics/utils/analyticsUtils'
import {ProductImage} from './partials/product-image'
import {WishlistButton} from './partials/wishlist-button'
import {ProductOverlayWrapper} from './partials/product-overlay-wrapper'
import {ProductInfo} from './partials/product-info'
import {QuickAddButton} from './partials/quick-add-button'
import type {CategoryType, CloudinaryProductVideo, ProductType, DyEngagementRequestBody} from '_types'
import {ProductImagesCarousel} from './partials/product-images-carousel'

export interface ProductTileContext {
    type: 'category' | 'search' | 'recommendation' | 'other'
    category?: CategoryType
    searchText?: string
    recoUUID?: string
    recommenderName?: string
}

const impressionCallbacks = new WeakMap<Element, () => void>()
let sharedImpressionObserver: IntersectionObserver | null = null

const scheduleNonCriticalTask = (task: () => void): void => {
    if (typeof window === 'undefined') {
        task()
        return
    }

    const browserWindow = window as Window & {
        requestIdleCallback?: (callback: () => void, options?: {timeout: number}) => number
    }

    if ('requestIdleCallback' in browserWindow && browserWindow.requestIdleCallback) {
        browserWindow.requestIdleCallback(task, {timeout: 400})
        return
    }

    window.setTimeout(task, 0)
}

const getSharedImpressionObserver = (): IntersectionObserver | null => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
        return null
    }

    if (sharedImpressionObserver) {
        return sharedImpressionObserver
    }

    sharedImpressionObserver = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting || entry.intersectionRatio < 0.5) {
                    return
                }

                const onVisible = impressionCallbacks.get(entry.target)
                if (!onVisible) {
                    return
                }

                onVisible()
                impressionCallbacks.delete(entry.target)
                sharedImpressionObserver?.unobserve(entry.target)
            })
        },
        {
            threshold: [0.5],
        },
    )

    return sharedImpressionObserver
}

const getCookieValue = (name: string): string | undefined => {
    if (typeof document === 'undefined') {
        return undefined
    }

    const cookieEntry = document.cookie
        .split(';')
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.startsWith(`${name}=`))

    if (!cookieEntry) {
        return undefined
    }

    const [, ...cookieValue] = cookieEntry.split('=')
    return decodeURIComponent(cookieValue.join('='))
}

export interface ProductTileProps {
    product: ProductType
    idx?: number
    hasLazy?: boolean
    dySlotId?: string
    listingLayout?: 'grid3' | 'grid2' | 'wishlist' | 'shoptheimage' | 'carousel'
    isFullWidth?: boolean
    analyticsList?: string
    addToCartOrigin?: 'recommendation' | 'einstein'
    shouldHideWishlistButton?: boolean
    variant?: 'wishlist' | 'shoptheimage'
    productTileContext?: ProductTileContext
    triggerImpression?: boolean
    onUpdateItemSize?: (variantId: string) => void
    onWishlistItemRemove?: () => void
    onAddProductToCart?: () => void
    showCarousel?: boolean
    showCover?: boolean
    showHover?: boolean
}

/**
 * @name ProductTile
 *
 * @component
 *
 * @description
 * Renders a product tile with various details such as image, price, wishlist button,
 * and quick add functionality. It also handles analytics events and supports features like size selection
 * and hover effects.
 *
 * @remarks
 * - It integrates with analytics for product impressions and quick cart actions.
 * - The wishlist functionality is handled via `useWishlistActions`.
 * - The component supports hover images, vertical videos, and size selection for products.
 *
 * @example
 * ```tsx
 * <ProductTile
 *   product={productData}
 *   idx={0}
 *   listingLayout="grid"
 * />
 * ```
 *
 * @param {ProductTileProps} props - Component properties
 * @param {ProductType} props.product - Product data object to display
 * @param {number} [props.idx=0] - Index of the product in a listing (used for analytics)
 * @param {boolean} [props.hasLazy] - Whether to lazy load images
 * @param {string} [props.listingLayout='grid3'] - Layout type ('grid2', 'grid3', 'full', 'horizontal')
 * @param {boolean} [props.isFullWidth] - Whether the tile should be full width
 * @param {string} [props.analyticsList] - List name for analytics
 * @param {boolean} [props.shouldHideWishlistButton] - Whether to hide the wishlist button
 * @param {string} [props.variant] - Variant type of the product tile (e.g., 'wishlist')
 * @param {function} [props.onWishlistItemRemove] - Callback to catch behaviour of wishlist item remove.
 * @param {function} [props.onAddProductToCart] - Callback to catch behaviour of add to cart item.
 *
 * @returns {JSX.Element} Rendered `ProductTile` component
 */
const ProductTile = React.memo(
    ({
        product,
        idx = 0,
        hasLazy,
        dySlotId,
        listingLayout = 'grid3',
        isFullWidth,
        analyticsList,
        addToCartOrigin,
        shouldHideWishlistButton,
        variant,
        productTileContext = {type: 'other'},
        triggerImpression,
        onUpdateItemSize,
        onWishlistItemRemove,
        onAddProductToCart,
        showCarousel = true,
        showCover = true,
        showHover = true,
    }: ProductTileProps) => {
        const styles = useMultiStyleConfig('ProductTile', {listingLayout}) as Record<string, StackProps & LinkProps & TextProps>
        const {analyticsProductImpression, analyticsProductClick, analyticsEventQuickCartAdd} = useAnalytics()
        const {isMobile, isTablet, isDesktop} = useViewport()
        const {site} = useMultiSite()
        const productTileAnalyticsContext = useProductTileAnalyticsContext()
        const einstein = useEinstein()
        const {showLoader, hideLoader} = useLoader()

        const [shouldOpenOverlay, setShouldOpenOverlay] = useState<boolean | undefined>(undefined)
        const [selectedVariant, setSelectedVariant] = useState<string | null>(product.id)
        const [selectedSize, setSelectedSize] = useState<string | null>(null)
        const shouldUseProduct = useMemo(() => variant !== 'wishlist' && shouldOpenOverlay, [shouldOpenOverlay])
        const productRef = React.useRef<HTMLDivElement>(null)
        const quickAddButtonRef = React.useRef<HTMLButtonElement>(null)

        const {data: _product, isLoading} = useProduct(
            {
                parameters: {
                    id: selectedVariant,
                    c_productTile: true,
                    expand: ['availability'],
                },
            },
            {enabled: !!shouldUseProduct},
        )

        const customProduct = !isLoading && _product ? _product : product

        const {activeVariation, productCurrency, isProductInStock, isProductSet, productLink} = getProductAnalyticsInfo(customProduct)
        // UI badge labels remain localized and must not consume the English analytics callout.
        const productUiTextTag = activeVariation?.textTag || customProduct?.c_textTag
        const deviceType = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'

        const tileAssetSize = useMemo(() => {
            return isFullWidth ? TILE_ASSET_SIZE[deviceType].full : TILE_ASSET_SIZE[deviceType][listingLayout]
        }, [isFullWidth, deviceType, listingLayout])

        const horizontalAssetSize = useMemo(() => {
            return TILE_ASSET_SIZE[deviceType].horizontal
        }, [deviceType])

        const productHorizontalImageSrc = product?.c_images?.horizontal?.url
        const productHorizontalImageAlt = product?.c_images?.horizontal?.alt
        const productHorizontalImage = useMemo(() => {
            return updateCloudinarySize(productHorizontalImageSrc, horizontalAssetSize)
        }, [productHorizontalImageSrc, horizontalAssetSize])

        const isHorizontalTile = Boolean(productHorizontalImage) && !isMobile && listingLayout === 'grid2' && isFullWidth

        // New responsive video contract (c_cloudinaryVideo) preferred; legacy single-URL kept as fallback.
        // `product` is the loosely-typed commerce-sdk ProductType, so read the custom field via a cast.
        const productCloudinaryVertical = (product as {c_cloudinaryVideo?: CloudinaryProductVideo})?.c_cloudinaryVideo?.vertical
        const productVerticalVideoSrc = product?.c_videos?.vertical
        const hasProductVideo = Boolean(productCloudinaryVertical?.sources?.length) || Boolean(productVerticalVideoSrc)
        const hasCoverImage = showCover && Boolean((activeVariation?.images || customProduct?.c_images)?.cover)
        // Suppress the hover image whenever a video (new OR legacy) will render.
        const showHoverImage = showHover && !hasProductVideo && !hasCoverImage
        const hasImageCarousel = showCarousel && Boolean((activeVariation?.images || customProduct?.c_images)?.showCarousel)

        const productImagesSrc = useMemo(() => {
            return (activeVariation?.images || customProduct?.c_images)?.large?.map((img) => img.url)
        }, [activeVariation?.images, customProduct?.c_images])

        const productImagesAlt = useMemo(() => {
            return (activeVariation?.images || customProduct?.c_images)?.large?.map((img) => img.alt)
        }, [activeVariation?.images, customProduct?.c_images])

        const productImages: Array<{src: string; alt: string}> = useMemo(() => {
            if (!productImagesSrc) return []
            const imagesToUse = hasImageCarousel ? productImagesSrc : productImagesSrc.slice(0, 1)
            return imagesToUse.map((imgSrc, index) => ({
                src: updateCloudinarySize(imgSrc, tileAssetSize),
                alt: productImagesAlt?.[index] || '',
            }))
        }, [productImagesSrc, productImagesAlt, tileAssetSize, hasImageCarousel])

        const productImageSrc =
            (showCover && activeVariation?.images?.cover) ||
            activeVariation?.images?.url ||
            activeVariation?.images?.large?.[0]?.url ||
            (showCover && customProduct?.c_images?.cover) ||
            customProduct?.c_images?.large?.[0]?.url

        const productImage: string = useMemo(() => {
            return !hasImageCarousel ? updateCloudinarySize(productImageSrc, tileAssetSize) : ''
        }, [productImageSrc, tileAssetSize])

        const productImageAlt = activeVariation?.images?.alt || activeVariation?.images?.large?.[0]?.alt || customProduct?.c_images?.large?.[0]?.alt

        // const productVerticalVideo = productVerticalVideoSrc && updateCloudinarySize(productVerticalVideoSrc, tileAssetSize) dpr is not applicable to video

        const hoverImageSrc = showHoverImage ? (activeVariation?.images || product?.c_images)?.large?.[1]?.url : undefined
        const hoverImage = useMemo(() => {
            return hoverImageSrc && updateCloudinarySize(hoverImageSrc, tileAssetSize)
        }, [hoverImageSrc, tileAssetSize])

        const toggleSizeSelector = useCallback(() => {
            setShouldOpenOverlay((prev) => {
                if (prev === false || prev === undefined) {
                    ;(document.querySelector('.openedSizeSelector') as HTMLElement)?.click()
                    quickAddButtonRef.current?.classList.add('openedSizeSelector')
                } else {
                    quickAddButtonRef.current?.classList.remove('openedSizeSelector')
                }
                return !prev
            })
        }, [])

        const sendAnalyticsImpression = () => {
            productRef.current?.setAttribute('data-impression', '')

            const analyticsProductObject = getProductImpressionAnalytics({
                product: customProduct,
                idx,
                tileAssetSize,
                isFullWidth,
                selectedSize,
                analyticsList,
                page: listingLayout === 'shoptheimage' ? 'editorial-shoptheimage' : 'plp',
                productSortingEngine: productTileAnalyticsContext?.productSortingEngine,
                productSortingType: productTileAnalyticsContext?.productSortingType,
            })

            analyticsProductImpression({
                currencyCode: productCurrency,
                template: listingLayout === 'shoptheimage' ? '' : listingLayout,
                impressions: analyticsProductObject,
            })
            // Dynamic Yield Product Impression Tracking
            if (typeof window !== 'undefined' && window.DY && typeof window.DY.API === 'function') {
                try {
                    window.DY.API('event', {
                        name: 'View Item',
                        properties: {
                            items: [{itemId: product.id || ''}],
                            source: 'search',
                        },
                    })
                } catch (error) {
                    console.error('[DY Tracking] Errore nel tracking impression:', error)
                }
            }
        }

        const sendDyEngagement = async (requestBody: DyEngagementRequestBody) => {
            if (typeof window === 'undefined' || !site?.id) {
                return
            }

            const response = await fetch('/dyEngagement', {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'content-type': 'application/json',
                },
                body: JSON.stringify({requestBody, siteId: site.id}),
            })

            if (!response.ok) {
                throw new Error(`Dynamic Yield API error: ${response.status} ${response.statusText}`)
            }
        }

        const onProductClick = () => {
            sendAnalyticsProductClick()

            // send einstein events
            const {type, category, searchText, recoUUID, recommenderName} = productTileContext

            if (type === 'search') {
                einstein.sendClickSearch(searchText, product)
            } else if (type === 'category') {
                einstein.sendClickCategory(category, product)
            } else if (type === 'recommendation') {
                einstein.sendClickReco({__recoUUID: recoUUID, recommenderName}, product)
            }

            // Dynamic Yield Product Click Tracking
            if (typeof window !== 'undefined' && window.DY && typeof window.DY.API === 'function' && (type === 'category' || type === 'search')) {
                try {
                    window.DY.API('event', {
                        name: 'Custom_Search_Slot_Click',
                        properties: {
                            provider: productTileAnalyticsContext?.productSortingEngine === 'dynamic_yield' ? 'DY' : 'SF',
                            search_query: '',
                            Product_SKU: product.id || '',
                            click_position: idx,
                        },
                    })
                } catch (error) {
                    console.error('[DY Tracking] Errore nel tracking del click:', error)
                }
            }

            if ((type === 'category' || type === 'search') && productTileAnalyticsContext?.productSortingEngine === 'dynamic_yield') {
                const dyEngagementPayload: DyEngagementRequestBody = {
                    user: {
                        active_consent_accepted: Boolean(window.DY?.userActiveConsent?.accepted),
                        dyid_server: getCookieValue('_dyid_server'),
                        dyid: getCookieValue('_dyid'),
                    },
                    session: {
                        dy: getCookieValue('_dyjsession'),
                    },
                    engagements: [
                        {
                            type: 'SLOT_CLICK',
                            slotId: dySlotId,
                        },
                    ],
                }
                void sendDyEngagement(dyEngagementPayload).catch((error) => {
                    console.error('[DY Tracking] Errore nella chiamata engagement via SSR:', error)
                })
            }
        }

        const sendAnalyticsProductClick = () => {
            const analyticsProductObject = getProductImpressionAnalytics({
                product: customProduct,
                idx,
                tileAssetSize,
                isFullWidth,
                selectedSize,
                analyticsList,
                page: listingLayout === 'shoptheimage' ? 'editorial-shoptheimage' : 'plp',
                productSortingEngine: productTileAnalyticsContext?.productSortingEngine,
                productSortingType: productTileAnalyticsContext?.productSortingType,
            })
            const {list, ...analyticsProductWithoutList} = analyticsProductObject

            analyticsProductClick({
                currencyCode: productCurrency,
                dimension25: listingLayout === 'shoptheimage' ? '' : listingLayout,
                actionField: {
                    list: list,
                },
                product: analyticsProductWithoutList,
            })
        }

        useEffect(() => {
            const node = productRef.current
            if (!node) {
                return
            }

            if (node.getAttribute('data-impression') === '') {
                return
            }

            const observer = getSharedImpressionObserver()
            if (!observer) {
                sendAnalyticsImpression()
                return
            }

            impressionCallbacks.set(node, () => {
                if (node.getAttribute('data-impression') === '') {
                    return
                }

                node.setAttribute('data-impression', '')

                scheduleNonCriticalTask(() => {
                    sendAnalyticsImpression()
                })
            })

            observer.observe(node)

            return () => {
                impressionCallbacks.delete(node)
                observer.unobserve(node)
            }
        }, [])

        useEffect(() => {
            if (triggerImpression) {
                sendAnalyticsImpression()
            }
        }, [triggerImpression])

        useEffect(() => {
            if (shouldOpenOverlay !== undefined) {
                const productId = customProduct?.productId || customProduct?.id

                analyticsEventQuickCartAdd({
                    accessType: 'tile',
                    action: shouldOpenOverlay ? 'open' : 'close',
                    label: productId,
                })
            }
        }, [shouldOpenOverlay])

        /**
         * Updates product in the wishlist when size is selected.
         */
        useEffect(() => {
            if (variant === 'wishlist' && selectedSize) {
                onUpdateItemSize?.(selectedSize)
            }
        }, [selectedSize])

        useEffect(() => {
            if (isLoading && shouldUseProduct) {
                showLoader()
            } else {
                hideLoader()
            }
        }, [isLoading, shouldUseProduct])

        return (
            <>
                <Box ref={productRef} {...styles.wrapper} data-testid={`product-tile-${product.productId}`}>
                    <Box {...styles.imageWrapper}>
                        <Link
                            as={RouteLink}
                            to={productLink}
                            {...styles.image}
                            onClick={onProductClick}
                            tabIndex={-1}
                            aria-hidden={true}
                            aria-label={product.c_ariaLabel}
                            data-testid={`product-tile-${product.productId}-link`}
                        >
                            {hasImageCarousel && !isHorizontalTile ? (
                                <ProductImagesCarousel
                                    productImages={productImages}
                                    productId={customProduct.id}
                                    analyticsProductId={customProduct?.c_analytics?.productId}
                                    analyticsList={analyticsList}
                                    isDesktop={isDesktop}
                                    hasLazy={hasLazy}
                                    styles={styles}
                                    showHover={Boolean((activeVariation?.images || customProduct?.c_images)?.showHover)}
                                    isOverlayOpen={Boolean(shouldOpenOverlay)}
                                />
                            ) : (
                                <ProductImage
                                    productImage={productImage}
                                    hoverImage={hoverImage}
                                    videoSrc={productCloudinaryVertical?.sources?.length ? undefined : productVerticalVideoSrc}
                                    sources={productCloudinaryVertical?.sources}
                                    videoPoster={productCloudinaryVertical?.poster}
                                    isHorizontalTile={isHorizontalTile}
                                    isDesktop={isDesktop}
                                    alt={productImageAlt}
                                    hasLazy={hasLazy}
                                    horizontalImage={productHorizontalImage}
                                    horizontalImageAlt={productHorizontalImageAlt}
                                    styles={styles}
                                />
                            )}
                        </Link>

                        {productUiTextTag && (
                            <Box {...styles.tagLabel}>
                                <Tag variant="opacity">{productUiTextTag}</Tag>
                            </Box>
                        )}
                        {!isProductSet && !shouldHideWishlistButton && (
                            <WishlistButton
                                product={customProduct}
                                variant={variant}
                                selectedSize={selectedSize}
                                isMobile={isMobile}
                                isTablet={isTablet}
                                onWishlistItemRemove={onWishlistItemRemove}
                                styles={styles}
                            />
                        )}
                        {shouldOpenOverlay && (
                            <ProductOverlayWrapper
                                product={customProduct}
                                variant={variant}
                                selectedVariant={selectedVariant}
                                selectedSize={selectedSize}
                                setSelectedSize={setSelectedSize}
                                setSelectedVariant={setSelectedVariant}
                                toggleSizeSelector={toggleSizeSelector}
                                isInStock={isProductInStock}
                                productLink={productLink}
                                analyticsList={analyticsList}
                                addToCartOrigin={addToCartOrigin}
                                idx={idx}
                                tileAssetSize={tileAssetSize}
                                isFullWidth={isFullWidth}
                                onAddProductToCart={onAddProductToCart}
                                onUpdateItemSize={onUpdateItemSize}
                            />
                        )}
                    </Box>
                    <HStack {...styles.body} {...(isHorizontalTile ? styles.horizontalBody : {})}>
                        <ProductInfo
                            product={product}
                            customProduct={customProduct}
                            styles={styles}
                            selectedSizeCode={selectedSize}
                            onProductClick={sendAnalyticsProductClick}
                        />
                        {!isProductSet && (
                            <QuickAddButton
                                ref={quickAddButtonRef}
                                onClick={() => toggleSizeSelector()}
                                shouldOpenOverlay={shouldOpenOverlay}
                                listingLayout={listingLayout}
                                productName={product.c_shortDescription}
                                productId={product.id}
                            />
                        )}
                    </HStack>
                </Box>
            </>
        )
    },
)

ProductTile.displayName = 'ProductTile'

const ProductSkeleton = () => {
    const styles = useMultiStyleConfig('ProductTile') as Record<string, StackProps & LinkProps & TextProps>

    return (
        <Box {...styles.wrapper} data-testid="product-tile-skeleton">
            <Box {...styles.skeletonImageWrapper}>
                <Skeleton {...styles.skeletonImage} />
            </Box>
            <HStack {...styles.body}>
                <VStack {...styles.details}>
                    <Skeleton {...styles.skeletonTitle} />
                    <Skeleton {...styles.skeletonPrice} />
                </VStack>
            </HStack>
        </Box>
    )
}

export {ProductTile, ProductSkeleton}
